import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';

import { getMutationsDeclarations } from './mutations-generator';
import { createCancellablePromise } from '..';
import StoreModuleService from '../../modules/StoreModuleService';
import { setLoading } from '../../modules/main/actions';
import { resetWidgetFieldTestStatus } from '../../modules/selectedNode/actions';
import { isMiniApp } from '../../modules/selectedNode/getters';
import { getWidgetData, saveWidget as rootSaveWidget, assignWidgetFieldCredentialsSourceField } from '../../modules/widgets/actions';
import { getWidget } from '../../modules/widgets/getters';
import api from '@/api';
import WidgetFieldService from '@/components/BlockEditorWidget/WidgetFieldService';
import { getNodeData } from '@/store/modules/nodes/actions.js';
import { selectedWorkflowId } from '@/store/modules/selectedWorkflow/getters.js';
import { getConfiguration } from '@/store/modules/workflowConfigurations/actions.js';
import { applyDrag } from '@/utils/helpers';

const {
	SET_EDIT_FIELD,
	SET_FIELD,
	SET_WIDGET_ID,
	SET_WIDGET_TITLE,
	DELETE_WIDGET,
	SET_WIDGET_METADATA,
	SET_WIDGET,
	UPDATE_WIDGET,
	SET_WIDGET_PROMISE,
	SET_WIDGET_WORKFLOW_VERSION_ID,
	IS_SAVING,
	IS_LOADING_WIDGET_FIELDS,
	SET_NODE_ID,
} = getMutationsDeclarations();

/**
 * IMPORTANT TO ANYONE WHO ADD / CHANGE / REMOVE ANY ACTION HERE
 * ANY CHANGES TO THE ACTIONS LIST NEEDS TO BE REFLECTED IN
 * 'WidgetEditor', 'generalConfigurationWidgetEditor' and 'userConfigurationWidgetEditor
 * and any other if new one is added
 */

export const getActionsDeclarations = (moduleName) => {
	const buildWidgetEditorActionObject = (action) => StoreModuleService.buildActionObject(moduleName, action);
	// actions declarations
	const reset = buildWidgetEditorActionObject('reset');
	const initWidgetFields = buildWidgetEditorActionObject('initWidgetFields');
	const setEditorState = buildWidgetEditorActionObject('setEditorState');
	const setFields = buildWidgetEditorActionObject('setFields');
	const duplicateField = buildWidgetEditorActionObject('duplicateField');
	const deleteWidgetField = buildWidgetEditorActionObject('deleteWidgetField');
	const moveWidgetFieldConfiguration = buildWidgetEditorActionObject('moveWidgetFieldConfiguration');
	const setEditField = buildWidgetEditorActionObject('setEditField');
	const updateWidgetFieldEdit = buildWidgetEditorActionObject('updateWidgetFieldEdit');
	const updateFieldsOrder = buildWidgetEditorActionObject('updateFieldsOrder');
	const addField = buildWidgetEditorActionObject('addField');
	const saveWidget = buildWidgetEditorActionObject('saveWidget');
	const saveWidgetDebounce = buildWidgetEditorActionObject('saveWidgetDebounce');
	const saveWidgetAPI = buildWidgetEditorActionObject('saveWidgetAPI');
	const setWidgetTitle = buildWidgetEditorActionObject('setWidgetTitle');
	const setWidgetMetadata = buildWidgetEditorActionObject('setWidgetMetadata');
	const setWidgetOption = buildWidgetEditorActionObject('setWidgetOption');
	const setWidgetStyle = buildWidgetEditorActionObject('setWidgetStyle');
	const editFirstField = buildWidgetEditorActionObject('editFirstField');
	const syncWidgetEditorWithWidgetsStore = buildWidgetEditorActionObject('syncWidgetEditorWithWidgetsStore');
	const createSourceFieldForEditField = buildWidgetEditorActionObject('createSourceFieldForEditField');

	const declarations = {
		reset,
		initWidgetFields,
		setEditorState,
		setFields,
		duplicateField,
		deleteWidgetField,
		moveWidgetFieldConfiguration,
		setEditField,
		updateWidgetFieldEdit,
		updateFieldsOrder,
		addField,
		saveWidget,
		saveWidgetDebounce,
		saveWidgetAPI,
		setWidgetTitle,
		setWidgetMetadata,
		setWidgetOption,
		setWidgetStyle,
		editFirstField,
		syncWidgetEditorWithWidgetsStore,
		createSourceFieldForEditField,
	};

	return declarations;
};

export const buildActions = (moduleName, state) => {
	const {
		reset,
		initWidgetFields,
		setEditorState,
		setFields,
		duplicateField,
		deleteWidgetField,
		moveWidgetFieldConfiguration,
		setEditField,
		updateWidgetFieldEdit,
		updateFieldsOrder,
		addField,
		saveWidget,
		saveWidgetDebounce,
		saveWidgetAPI,
		setWidgetTitle,
		setWidgetMetadata,
		setWidgetOption,
		setWidgetStyle,
		editFirstField,
		syncWidgetEditorWithWidgetsStore,
		createSourceFieldForEditField,
	} = getActionsDeclarations(moduleName);

	const actions = {
		...StoreModuleService.createDefaultActions(),
		async [initWidgetFields.action]({ dispatch, commit }, payload) {
			const { id: widgetId, workflowVersionId, nodeId } = payload;
			if (widgetId !== state.widgetId || workflowVersionId !== state.workflowVersionId || nodeId !== state.nodeId) {
				await dispatch(reset.action); // when entering edit mode, first clear widget fields
			}
			commit(SET_WIDGET_ID, widgetId);
			commit(SET_WIDGET_WORKFLOW_VERSION_ID, workflowVersionId);
			commit(SET_NODE_ID, nodeId);
			commit(IS_LOADING_WIDGET_FIELDS, true);
			await dispatch(getWidgetData.fullPath, { id: widgetId, workflowVersionId, forceGet: true }, { root: true });
			await dispatch(syncWidgetEditorWithWidgetsStore.action);
			commit(IS_LOADING_WIDGET_FIELDS, false);
		},
		async [syncWidgetEditorWithWidgetsStore.action]({ dispatch, rootGetters }) {
			const ret = rootGetters[getWidget.fullPath](state.widgetId);
			if (ret) {
				await dispatch(setEditorState.action, { data: ret });
			}
		},
		async [setEditorState.action]({ commit, dispatch, rootGetters }, payload) {
			const { data } = payload;
			const { widget_title: widgetTitle, metadata } = data;

			commit(SET_WIDGET, data);
			commit(SET_WIDGET_TITLE, widgetTitle);
			commit(SET_WIDGET_METADATA, metadata);
			const updatedEditedField = data.fields.find((f) => f.id === state.editField?.id);
			// if editedField is missing or not set, we'll set null on editField
			commit(SET_EDIT_FIELD, updatedEditedField);

			// If we are in mini-app mode, by default edit the first field in widget.
			if (rootGetters[isMiniApp.fullPath]) {
				await dispatch(editFirstField.action, null);
			}
		},
		[setFields.action]({ commit }, payload) {
			const { fields } = payload;
			const newWidget = { ...state.widget, fields };
			commit(SET_WIDGET, newWidget);
		},
		[duplicateField.action]({ dispatch }) {
			if (!state.editField) {
				return;
			}
			const fieldIndex = state.widget.fields.findIndex((f) => f.id === state.editField.id);
			dispatch(addField.action, { fieldToCopy: state.editField, addedIndex: fieldIndex + 1, removedIndex: null });
			console.log('duplicateField');
		},
		async [deleteWidgetField.action]({ commit, dispatch }, payload) {
			if (!state.editField) {
				console.log('deleteWidget - no widget is selected. check me!');
				return;
			}
			const widgetId = state.editField.id;
			if (!payload.dontDeleteTestResponse) {
				await dispatch(resetWidgetFieldTestStatus.fullPath, widgetId, { root: true });
			}

			commit(DELETE_WIDGET, widgetId);
			dispatch(setEditField.action, null);
			await dispatch(saveWidget.action);
		},
		async [moveWidgetFieldConfiguration.action]({ commit, dispatch, rootGetters }, isGeneralConfiguration) {
			if (!state.editField) {
				console.log('moveWidgetFieldConfiguration - no widget is selected. check me!');
				return;
			}
			const workflowId = rootGetters[selectedWorkflowId.fullPath];
			const fieldId = state.editField.id;
			const sourceType = isGeneralConfiguration ? 'general' : 'user-specific';
			const destinationType = isGeneralConfiguration ? 'user-specific' : 'general';
			await api.moveWidgetFieldConfiguration(workflowId, fieldId, sourceType, destinationType);
			commit(DELETE_WIDGET, fieldId);
			dispatch(setEditField.action, null);
			await dispatch(saveWidget.action);
		},
		[setEditField.action]({ commit }, field) {
			commit(SET_EDIT_FIELD, field);
		},
		[updateWidgetFieldEdit.action]({ commit, dispatch }, field) {
			commit(IS_SAVING, true);
			const fieldIndex = state.widget.fields.findIndex((f) => f.id === field.id);
			if (field && fieldIndex > -1) {
				if (field.field_type === 'search') {
					// let updatedTitle = ''

					if (field.metadata.source.action) {
						// const actionTitle = rootGetters[getAction.fullPath](field.metadata.source.action).title
						// const appData = rootGetters[getService.fullPath](field.metadata.source.service)
						// updatedTitle = `Search ${appData.service_name} ${actionTitle}`
					} else if (field.metadata.source.service) {
						// const appData = rootGetters[getService.fullPath](field.metadata.source.service)
						// updatedTitle = `Search ${appData.service_name}`
					} else {
						// updatedTitle = 'Search'
					}

					if (!('identifier' in field.metadata.options)) {
						field.metadata.options.identifier = { value: field.metadata.options.title.value };
					}
					// const needToUpdateIdentifier = field.metadata.options.identifier.value === field.metadata.options.title.value

					// field.metadata.options.title.value = updatedTitle
					// if (needToUpdateIdentifier) {
					//   field.metadata.options.identifier.value = updatedTitle
					// }
				}
				commit(SET_FIELD, { index: fieldIndex, field });
				commit(SET_EDIT_FIELD, field);
			}
			dispatch(saveWidget.action);
		},
		async [updateFieldsOrder.action]({ dispatch }, dropResult) {
			dispatch(setFields.action, { fields: applyDrag(state.widget.fields, dropResult) });
			await dispatch(saveWidget.action);
		},
		async [addField.action]({ dispatch }, dropResult) {
			const { payload: fieldData, fieldToCopy } = dropResult;

			// if fieldToCopy provided, duplicate provided field
			// otherwise just create new field out of the current field_type
			const newWidgetField = fieldToCopy
				? { ...fieldToCopy, id: null }
				: WidgetFieldService.populateDefaultWidgetValuesIfNeeded({ field_type: fieldData?.type });

			const oldFields = cloneDeep(state.widget.fields);

			dispatch(setFields.action, { fields: applyDrag(state.widget.fields, dropResult, newWidgetField) });

			await dispatch(saveWidgetAPI.action); // No debounce, to prevent double creation at one call and then choose the new one
			const newFields = cloneDeep(state.widget.fields);
			var startIndex = 0;
			var newField = null;
			while (startIndex === 0) {
				if (newFields.length) {
					const curField = cloneDeep(newFields[startIndex]);
					var idxOldField = oldFields.findIndex((oldField) => oldField.id === curField.id);
					if (idxOldField > -1) {
						// Existed before creation
						oldFields.splice(idxOldField, 1);
						newFields.splice(startIndex, 1);
					} else {
						// found the new one
						newField = curField;
						startIndex++;
					}
				} else {
					startIndex++;
				}
			}
			if (newField) {
				await dispatch(setEditField.action, newField);
			}

			// this.droppedFields = applyDrag(this.droppedFields, dropResult)
		},
		[saveWidget.action]({ commit, dispatch }) {
			if (state.saveWidgetPromise) {
				state.saveWidgetPromise.cancel();
				commit(SET_WIDGET_PROMISE, null);
			}
			dispatch(saveWidgetDebounce.action);
		},
		[saveWidgetDebounce.action]: debounce(({ dispatch }) => dispatch(saveWidgetAPI.action), 1350),
		// eslint-disable-next-line no-unused-vars
		[saveWidgetAPI.action]({ commit, dispatch }) {
			const fieldsToSave = cloneDeep(state.widget.fields);
			for (let index = 0; index < fieldsToSave.length; index++) {
				const element = fieldsToSave[index];
				if (element) {
					element.order = index;
				}
			}
			dispatch(setLoading.fullPath, { value: true }, { root: true });
			const saveWidgetPromise = createCancellablePromise(() =>
				dispatch(
					rootSaveWidget.fullPath,
					{
						data: {
							id: state.widgetId,
							fields: fieldsToSave,
							title: state.widgetTitle,
							metadata: state.widgetMetadata,
							workflowVersionId: state.workflowVersionId,
						},
					},
					{ root: true }
				)
			);
			saveWidgetPromise.promise.then(async (ret) => {
				if (ret) {
					if (ret.error) {
						return;
					}
					commit(SET_WIDGET_PROMISE, null);
					dispatch(setFields.action, { fields: ret.fields });
					commit(UPDATE_WIDGET, { fields: ret.fields, metadata: ret.metadata });

					const affectedNodesIds = ret.affected_node_ids || [];
					await Promise.all(
						// TODO: DONT GO LIVE WITH THAT. LETS ADD BE ENDPOINT TO GET_NODES_BY_IDS
						affectedNodesIds.map((nodeId) => dispatch(getNodeData.fullPath, { id: nodeId, forceGet: true }, { root: true }))
					);
					commit(IS_SAVING, false);
				}
			});
			saveWidgetPromise.promise.finally(() => {
				dispatch(setLoading.fullPath, { value: false }, { root: true });
			});
			commit(SET_WIDGET_PROMISE, saveWidgetPromise);
			return saveWidgetPromise.promise;
		},
		[setWidgetTitle.action]({ commit, dispatch }, value) {
			commit(SET_WIDGET_TITLE, value);
			dispatch(saveWidget.action);
		},
		[setWidgetMetadata.action]({ commit, dispatch }, value) {
			commit(SET_WIDGET_METADATA, value);
			dispatch(saveWidget.action);
		},
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		[setWidgetOption.action]({ commit, dispatch }, payload) {
			const { value, key } = payload;
			const newOption = {};
			newOption[key] = value;
			const newOptions = Object.assign({}, state.widgetMetadata.options || {}, newOption);
			dispatch(setWidgetMetadata.action, Object.assign({}, state.widgetMetadata, { options: newOptions }));
		},
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		[setWidgetStyle.action]({ commit, dispatch }, payload) {
			const { value, key } = payload;
			const newStyle = {};
			newStyle[key] = value;
			const newStyles = Object.assign({}, state.widgetMetadata.style || {}, newStyle);
			dispatch(setWidgetMetadata.action, Object.assign({}, state.widgetMetadata, { style: newStyles }));
		},
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		[editFirstField.action]({ commit, dispatch, state }) {
			if (state.widget.fields.length > 0) {
				const firstField = state.widget.fields[0];
				dispatch(setEditField.action, firstField);
			}
		},
		async [createSourceFieldForEditField.action]({ dispatch }, { configurationId }) {
			const ret = await dispatch(
				assignWidgetFieldCredentialsSourceField.fullPath,
				{
					widgetFieldId: state.editField.id,
					workflowVersionId: state.workflowVersionId,
				},
				{ root: true }
			);
			// Sync widget data.
			await dispatch(
				getWidgetData.fullPath,
				{ id: state.widgetId, workflowVersionId: state.workflowVersionId, forceGet: true },
				{ root: true }
			);
			await dispatch(syncWidgetEditorWithWidgetsStore.action);
			if (configurationId) {
				await dispatch(getConfiguration.fullPath, { id: configurationId, forceGet: true }, { root: true });
			}
			return ret;
		},
	};

	return actions;
};
